import { useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import type { CardMenuItem } from '@tempmee/core';
import { Button, Link } from '@tempmee/react';
import { AdditionalDetails, LargeShiftDetails, Receipt } from '@tempmee/react-blocks';
import { OutlineChatAlt2Icon, OutlineTrashIcon } from '@tempmee/react-icons';
import LegacyJobDetails from '~/components/JobDetails/JobDetails';
import { useCancelJobMutation } from '~/domain/Jobs';
import { useMediaQuery } from '~/hooks';
import { routes } from '~/routes';
import { BookedProfessionalPanel } from '../BookedProfessionalPanel';
import { COLLAPSE_BREAKPOINT, DetailsPageShell } from '~/components/shared/DetailsPage';
import { useBookedProfessionalCredentials } from '../shared/hooks/useBookedProfessionalCredentials';
/*
 * The COMPLETED page's hook, under the name this page needs it by: it fetches the
 * profile of the professional named on `offer_final_reciever`, which is the same
 * job whether the shift is booked or finished. Imported rather than copied so one
 * professional cannot be rendered from two different fetches; the rename to a
 * shared `useBookedProfessional` belongs with the other cross-state promotions
 * once every state page has landed.
 */
import { useCompletedProfessional as useBookedProfessional } from '../CompletedShiftDetailsPage/hooks/useCompletedProfessional';
import {
  toCompletedReceiptLines,
  toCompletedReceiptTotal,
} from '../CompletedShiftDetailsPage/utils/completedShiftView';
import type { IShiftDetailsPageProps } from '../shiftDetailsPages';
import { getProfessionalName } from '~/utils/professional';
import { EM_DASH, toAdditionalDetailsRows } from '../shared/utils/shiftPanelsProps';
import { CancelScheduledShiftPopup } from './components/CancelScheduledShiftPopup';
import { hasTransactionHistory } from '../shared/utils/transactionHistory';

import {
  canCancelShift,
  canMessageProfessional,
  toScheduledShiftSummary,
  type TScheduledShiftJob,
} from './utils/scheduledShiftView';


/**
 * The redesigned SCHEDULED Shift Details page body.
 *
 * A confirmed shift is about the professional and the money: they head the
 * primary column with their full profile beneath, and the sidebar carries the
 * shift summary, its attributes and the receipt. Reached through
 * `ShiftDetailsRedesignGate`, which has already loaded the job and matched its
 * status, so there is no loading or not-found state here.
 *
 * The status label and colour are not decided here — `'scheduled'` is handed to
 * `LargeShiftDetails`, which reads `SHIFT_STATUS_META` from `@tempmee/core`.
 *
 * Deliberately **without** the End of Day / rating redirect the COMPLETED page
 * runs. The legacy `JobDetails.js` applies that redirect with no status gate, so
 * it fires on a `filled` shift too; a shift that has not happened yet has no
 * hours to confirm and no professional to rate, so reproducing it here would send
 * the office to End of Day for a shift still in the future. See the note on
 * `useCompletedFeedbackRedirect` — the legacy behaviour is reported separately,
 * not carried forward.
 *
 * Both sidebar panels below the summary ship **collapsed**, per the Figma's 56px
 * headers and the same decision the other states took.
 */
function ScheduledShiftDetailsRedesign({ job }: IShiftDetailsPageProps) {
  const scheduledShift = job as TScheduledShiftJob;
  const navigate = useNavigate();
  const [isCollapsed] = useMediaQuery([COLLAPSE_BREAKPOINT]);
  const { mutate: cancelJob, isPending: isCancelling } = useCancelJobMutation();

  const { professional } = useBookedProfessional(job);

  /*
   * The cancelled page's hook: a booked shift names its professional, so there is
   * no applicant pool to search — this asks the credentials domain directly.
   */
  const { authorizations } = useBookedProfessionalCredentials({
    job,
    professional: professional ?? job.offer_final_reciever,
  });

  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const canMessage = canMessageProfessional(scheduledShift);
  const canCancel = canCancelShift(scheduledShift);

  /*
   * The legacy pushes the shift id as `location.state` alongside the path
   * parameter; the chat screen reads it from there, so both are sent.
   */
  const openChat = () =>
    navigate(generatePath(routes.chat, { jobId: job.id }), { state: { jobId: job.id } });

  const actions: CardMenuItem[] = useMemo(() => {
    const items: CardMenuItem[] = [];

    if (canMessage) {
      items.push({
        label: 'Message Professional',
        icon: <OutlineChatAlt2Icon />,
        testID: 'shift-details-message-action',
        onSelect: openChat,
      });
    }

    if (canCancel) {
      items.push({
        label: 'Cancel',
        icon: <OutlineTrashIcon />,
        testID: 'shift-details-cancel-action',
        onSelect: () => setIsCancelOpen(true),
      });
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canMessage, canCancel, job.id]);

  return (
    <>
      <DetailsPageShell
        testIDPrefix="shift-details"
        actionsLabel="Shift actions"
        title="Scheduled Shift Details"
        actions={actions}
        onBack={() => navigate(-1)}
        summary={<LargeShiftDetails {...toScheduledShiftSummary(scheduledShift)} />}
        primary={
          <BookedProfessionalPanel
            job={job}
            topperStyle="scheduled"
            testIDPrefix="scheduled"
            professional={professional}
            authorizations={authorizations}
            isCollapsed={isCollapsed}
            /*
             * The Figma's own control, in the slot the topper stacks below `@md`.
             * `undefined` rather than an empty array when the window has closed:
             * the block drops the actions track entirely, and an empty one still
             * costs its column gap.
             */
            actions={
              canMessage
                ? [
                  <Button
                      key="message"
                      variant="outlined"
                      color="secondary"
                      onClick={openChat}
                      data-testid="message-professional-button"
                      data-cy="message-professional-button"
                    >
                    Message Professional
                  </Button>,
                  ]
                : undefined
            }
          />
        }
        sidebar={
          <>
            <AdditionalDetails
              variant="scheduled-complete"
              {...toAdditionalDetailsRows(
                scheduledShift,
                // The shift was confirmed by somebody, so the row stays even when
                // the payload does not name them — an em dash, not a dropped row.
                getProfessionalName(scheduledShift.confirmPerformedBy) || EM_DASH,
              )}
              defaultOpen={false}
            />
            <Receipt
              lines={toCompletedReceiptLines(scheduledShift)}
              total={toCompletedReceiptTotal(scheduledShift)}
              defaultOpen={false}
              totalAction={
                hasTransactionHistory(scheduledShift) ? (
                  <Link
                    as="button"
                    /*
                     * `as="button"` with `useNavigate`, not an `href`: the DS
                     * `Link` renders a real anchor, and an in-portal href would
                     * reload the whole SPA.
                     */
                    onClick={() =>
                      navigate(generatePath(routes.transactionHistoryDetails, { jobId: job.id }))
                    }
                    data-testid="transaction-history-link"
                    data-cy="transaction-history-link"
                  >
                    Transaction History
                  </Link>
                ) : undefined
              }
            />
          </>
        }
      />

      {isCancelOpen && (
        <CancelScheduledShiftPopup
          job={job}
          isPending={isCancelling}
          onClose={() => setIsCancelOpen(false)}
          onConfirm={() => {
            /*
             * `redirect: true` — the shift-details cancel leaves the page for the
             * cancellation flow, which is what the legacy popup does. Only the
             * list card cancels in place.
             */
            cancelJob({ jobId: job.id, redirect: true });
            setIsCancelOpen(false);
          }}
        />
      )}
    </>
  );
}

/**
 * SCHEDULED Shift Details, as registered in `SHIFT_DETAILS_PAGES`.
 *
 * A booked onsite interview is a different page: the legacy screen offers the
 * permanent-hire decision and titles itself for an interview, none of which the
 * shift redesign has a frame for. Until it does, booked interviews stay on the
 * screen they have today — the same split the OPEN and COMPLETED pages make.
 *
 * A component boundary rather than an early return, so this page's professional
 * fetch and credentials query never run on the delegated path, where the legacy
 * screen runs its own.
 */
export function ScheduledShiftDetailsPage({ job }: IShiftDetailsPageProps) {
  if (job.isOnsiteInterview) {
    return <LegacyJobDetails />;
  }

  return <ScheduledShiftDetailsRedesign job={job} />;
}
